import { useState, useEffect } from "react";
import { axiosApi } from '../../api/setupApi';

const TYPE_ENDPOINTS = [
  () => '/convergence/getAlltype',
  () => '/convergence/types',
  () => '/convergence/byType',
  () => '/convergence/list'
];

const DEFAULT_PROGRAMS = [
  { program: "EPAHP", indicators: ["No. of Marketing Agreements", "Gross Sales (Php)"] },
  { program: "PCB Project - SLP", indicators: ["No. of Projects", "No. of Beneficiaries (IA Members)", "Gross Sales (Php)"] },
  { program: "Cash for Work", indicators: ["No. of Beneficiaries (IA Members)", "Cash Counterpart (Php)"] },
  { program: "Walang Gutom Program", indicators: ["No. of IA", "No. of Beneficiaries (IA Members)"] },
  { program: "Family Food Packs", indicators: ["No. of IA", "No. of Beneficiaries (IA Members)"] },
  { program: "Food for Work", indicators: ["No. of IA", "No. of Beneficiaries (IA Members)"] },
  { program: "Gulayan sa Pamayanan", indicators: ["No. of Beneficiaries (IA Members)"] },
  { program: "LAWA at BINHI", indicators: ["No. of IA", "IA Members"] },
  { program: "Financial Assistance", indicators: ["No. of Beneficiaries (IA Members)", "Area(ha)", "Cash Counterpart (Php)"] },
  { program: "Trainings", indicators: ["No. of Trainings", "No. of IA", "No. of Batches", "No. of Farmers trained (IA Members)"] }
];

const FALLBACK_TYPE_MAP = (() => {
  const allIndicators = new Set();
  DEFAULT_PROGRAMS.forEach(({ indicators }) => indicators.forEach((i) => allIndicators.add(i)));
  return {
    defaultType: {
      programs: DEFAULT_PROGRAMS.map(({ program }) => program),
      indicators: Array.from(allIndicators)
    }
  };
})();

function splitCSV(str) {
  if (!str) return [];
  return str
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeTypePayload(payload) {
  if (!payload) return {};

  const grouped = {};

  const addEntry = (type, programs = [], indicators = []) => {
    if (!type) return;
    const existing = grouped[type] || { programs: new Set(), indicators: new Set() };
    programs.forEach((p) => existing.programs.add(p));
    indicators.forEach((i) => existing.indicators.add(i));
    grouped[type] = existing;
  };

  const mapToObject = () =>
    Object.entries(grouped).reduce((acc, [type, sets]) => {
      acc[type] = {
        programs: Array.from(sets.programs),
        indicators: Array.from(sets.indicators)
      };
      return acc;
    }, {});

  if (Array.isArray(payload)) {
    payload.forEach((entry) => {
      if (!entry) return;
      const { type, programs, indicators, program, indicator } = entry;
      const progList = splitCSV(programs).concat(program ? [program] : []);
      const indicatorList = splitCSV(indicators).concat(indicator ? [indicator] : []);
      addEntry(type, progList, indicatorList);
    });
    return mapToObject();
  }

  if (payload.success && Array.isArray(payload.data)) {
    return normalizeTypePayload(payload.data);
  }

  if (payload.success && payload.data && typeof payload.data === 'object') {
    Object.entries(payload.data).forEach(([type, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          const progList = splitCSV(entry?.programs).concat(entry?.program ? [entry.program] : []);
          const indicatorList = splitCSV(entry?.indicators).concat(entry?.indicator ? [entry.indicator] : []);
          addEntry(entry?.type || type, progList, indicatorList);
        });
      }
    });
    return mapToObject();
  }

  if (Array.isArray(payload.rows)) {
    return normalizeTypePayload(payload.rows);
  }

  return {};
}

export default function useTypeListConvergence() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchTypes() {
      setLoading(true);
      setError(null);
      const tried = [];
      let lastError = null;

      for (const endpointFactory of TYPE_ENDPOINTS) {
        const endpoint = endpointFactory();
        tried.push(endpoint);
        try {
          const { data: responseData } = await axiosApi.get(endpoint);
          const normalized = normalizeTypePayload(responseData);
          if (isMounted) {
            setData(normalized);
            setError(null);
            setLoading(false);
          }
          return;
        } catch (err) {
          lastError = err;
          const status = err?.response?.status;
          const recoverable = !status || [400, 404, 405].includes(status);
          if (!recoverable) {
            break;
          }
        }
      }

      if (isMounted) {
        if (lastError) {
          console.warn('Falling back to built-in convergence type list.', lastError);
        }
        setData(FALLBACK_TYPE_MAP);
        setError(null);
        setLoading(false);
      }
    }

    fetchTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  return { groupedConvergenceData: data, loading, error };
}
